import React, { useRef, useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Textarea } from '../../ui/textarea';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { MessageSquareText, Sparkles } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { config } from '../../../config';
import { ReportBlock } from '../../../types/report-builder';
import ReactMarkdown from 'react-markdown';

interface ReportChatPanelProps {
  onBlocksUpdate: (blocks: ReportBlock[]) => void;
  onGeneratingChange: (isGenerating: boolean) => void;
  embedded?: boolean;
}

const SUGGESTED_PROMPTS = [
  'Top root causes in my default collection over the last 6 months',
  'Taxonomy breakdown across all dimensions for my default collection',
  'Conduct score distribution and high-risk label analysis',
  'Build a report on the FOS corpus — outcomes, trends, and top products',
];

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
};

const ReportChatPanel: React.FC<ReportChatPanelProps> = ({
  onBlocksUpdate,
  onGeneratingChange,
  embedded = false,
}) => {
  const { token } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const assistantMessageIdRef = useRef<string | null>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    onGeneratingChange(isLoading);
  }, [isLoading, onGeneratingChange]);

  useEffect(() => {
    onBlocksUpdate(blocks);
  }, [blocks, onBlocksUpdate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const appendAssistantText = (deltaText: string) => {
    setMessages((prev) => {
      const assistantId = assistantMessageIdRef.current;
      if (!assistantId) {
        const newId = `assistant-${Date.now()}`;
        assistantMessageIdRef.current = newId;
        return [...prev, { id: newId, role: 'assistant', text: deltaText }];
      }

      return prev.map((message) =>
        message.id === assistantId
          ? { ...message, text: message.text + deltaText }
          : message
      );
    });
  };

  const parseStreamLine = (line: string) => {
    if (!line) return;

    if (line.startsWith('s:')) {
      try {
        const payload = JSON.parse(line.slice(2)) as { sessionId?: string };
        if (payload.sessionId) {
          sessionIdRef.current = payload.sessionId;
        }
      } catch {
        // ignore malformed session line
      }
      return;
    }

    if (line.startsWith('0:')) {
      const payload = line.slice(2);
      try {
        const textChunk = JSON.parse(payload);
        if (typeof textChunk === 'string') {
          appendAssistantText(textChunk);
        }
      } catch (error) {
        console.error('Failed to parse text chunk', error, line);
      }
      return;
    }

    if (line.startsWith('c:')) {
      const payload = line.slice(2);
      try {
        const cmd = JSON.parse(payload);
        if (cmd.action === 'clear') {
          setBlocks([]);
        } else if (cmd.action === 'remove' && cmd.blockId) {
          setBlocks((prev) => prev.filter((b) => b.id !== cmd.blockId));
        } else if (cmd.action === 'move' && cmd.blockId) {
          setBlocks((prev) => {
            const idx = prev.findIndex((b) => b.id === cmd.blockId);
            if (idx === -1) return prev;
            const targetIdx = cmd.direction === 'up' ? idx - 1 : idx + 1;
            if (targetIdx < 0 || targetIdx >= prev.length) return prev;
            const next = [...prev];
            [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
            return next;
          });
        }
      } catch (error) {
        console.error('Failed to parse canvas command', error, line);
      }
      return;
    }

    if (line.startsWith('9:')) {
      const payload = line.slice(2);
      try {
        const parsed = JSON.parse(payload) as { output?: unknown };
        const output = parsed.output as ReportBlock | undefined;
        if (output && typeof output === 'object' && 'id' in output && 'type' in output) {
          setBlocks((prev) => [...prev, output]);
        }
      } catch (error) {
        console.error('Failed to parse tool result chunk', error, line);
      }
      return;
    }

    if (line.startsWith('e:')) {
      setIsLoading(false);
      assistantMessageIdRef.current = null;
    }
  };

  const streamReport = async (promptText: string, priorMessages: ChatMessage[]) => {
    setIsLoading(true);
    assistantMessageIdRef.current = null;

    try {
      let body: Record<string, unknown>;
      if (sessionIdRef.current) {
        body = {
          session_id: sessionIdRef.current,
          messages: [{ role: 'user', content: promptText }],
        };
      } else {
        const history: { role: string; content: string }[] = priorMessages
          .filter((m) => m.text.trim())
          .map((m) => ({ role: m.role, content: m.text }));
        history.push({ role: 'user', content: promptText });
        body = { messages: history };
      }

      const response = await fetch(`${config.API_URL}/v1/report-builder/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok || !response.body) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to stream report response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          parseStreamLine(line.trim());
        }
      }

      if (buffer.trim()) {
        parseStreamLine(buffer.trim());
      }
    } catch (error) {
      console.error('Report stream failed', error);
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          text: 'I hit an error while generating this report. Please try again.',
        },
      ]);
    } finally {
      assistantMessageIdRef.current = null;
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput('');
    const currentMessages = [...messages];
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text },
    ]);
    void streamReport(text, currentMessages);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (prompt: string) => {
    setInput('');
    const currentMessages = [...messages];
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: prompt },
    ]);
    void streamReport(prompt, currentMessages);
  };

  return (
    <Card
      className={
        embedded
          ? 'flex h-full min-h-0 flex-col overflow-hidden rounded-none border-0 bg-card shadow-none'
          : 'flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border-border/70 bg-card shadow-[var(--shadow-card)]'
      }
    >
      <div
        className={
          embedded
            ? 'flex items-center justify-between gap-3 border-b border-border/70 bg-card px-4 py-3'
            : 'flex items-center justify-between gap-3 border-b border-border/70 bg-card px-4 py-3'
        }
      >
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--go-good-vibes-tint)] text-primary">
            <MessageSquareText className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Ask Juno
            </p>
            <p className="text-[12px] font-medium text-foreground">Report assistant</p>
          </div>
        </div>
        {isLoading && <span className="text-[11px] font-medium text-primary">Generating</span>}
      </div>

      <div
        ref={scrollContainerRef}
        className={
          embedded
            ? 'flex-1 overflow-y-auto bg-card px-3 py-3'
            : 'flex-1 overflow-y-auto bg-card px-4 py-4'
        }
      >
        {messages.length === 0 ? (
          <div className="flex min-h-full flex-col justify-center">
            <div className="mx-auto flex w-full max-w-[18.5rem] flex-col items-stretch">
              <div className="mb-6 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--go-good-vibes-tint)] text-primary">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-foreground">Ask Juno</h3>
                <p className="mt-1 text-[12px] leading-[1.65] text-muted-foreground">
                  Start with a prompt. Juno will build the first report blocks on the canvas.
                </p>
              </div>

              <div className="mb-2 flex items-center gap-2 px-1">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <p className="text-[12px] font-medium text-muted-foreground">Starter prompts</p>
              </div>

              <div className="space-y-2">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestion(prompt)}
                    className={
                      embedded
                        ? 'w-full rounded-xl border border-border/70 bg-card px-3 py-2.5 text-left text-[13px] leading-[1.55] text-foreground transition hover:border-primary/20 hover:bg-accent'
                        : 'w-full rounded-xl border border-border/70 bg-card px-3 py-2.5 text-left text-[13px] leading-[1.55] text-foreground transition hover:border-primary/20 hover:bg-accent'
                    }
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              if (!message.text.trim()) return null;
              return (
                <div
                  key={message.id}
                  className={message.role === 'user' ? 'flex justify-end' : 'flex gap-2'}
                >
                  {message.role === 'assistant' && (
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Sparkles className="h-3.5 w-3.5" />
                    </div>
                  )}
                  <div
                    className={
                      message.role === 'user'
                        ? 'max-w-[88%] rounded-2xl rounded-br-md bg-primary px-3 py-2.5 text-[13px] text-primary-foreground'
                        : 'max-w-[92%] text-[13px] text-foreground'
                    }
                  >
                    {message.role === 'assistant' ? (
                      <div className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1">
                        <ReactMarkdown>{message.text}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="leading-[1.65]">{message.text}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div className="flex gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                </div>
                <div className="rounded-2xl border border-border/70 bg-card px-3 py-2.5">
                  <div className="flex gap-1.5">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary/50" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary/50 [animation-delay:0.1s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary/50 [animation-delay:0.2s]" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={embedded ? 'border-t border-border/70 bg-card p-3' : 'border-t border-border/70 bg-card p-3'}>
        <div className="space-y-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the report you want to build..."
            className={
              embedded
                ? 'min-h-16 resize-none rounded-xl border-border/80 bg-card px-3 py-3 text-[13px] shadow-none focus-visible:ring-primary/20'
                : 'min-h-16 resize-none rounded-xl border-border/80 bg-card px-3 py-3 text-[13px] shadow-none focus-visible:ring-primary/20'
            }
            rows={2}
          />
          <div className="flex items-center justify-between gap-3">
            <div />
            <Button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="sm"
              className="gap-2 rounded-full px-4"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              Send
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ReportChatPanel;
